import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Divider } from 'src/components/Divider';
import { Typography } from 'src/components/Typography';
import { CartItem as CartItemType } from 'src/models/cart/_types';

import { PosConfigItemSelectorDialog } from './PosConfigItemSelectorDialog';

export const TestPage: React.FC = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savedItemLists, setSavedItemLists] = useState<
    { items: CartItemType[]; mode: string }[]
  >([]);
  const [isKioskMode, setIsKioskMode] = useState(false);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSaveItems = (items: CartItemType[]) => {
    setSavedItemLists((prev) => [
      ...prev,
      { items, mode: isKioskMode ? 'Kiosk' : 'POS' },
    ]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('PosConfig Test Page')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t(
          'This is a test page for the PosConfigItemSelectorDialog component.'
        )}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Checkbox
          useSwitch
          checked={isKioskMode}
          onChange={(e) => setIsKioskMode(e.target.checked)}
          label={`Mode: ${isKioskMode ? 'Kiosk' : 'POS'}`}
        />
      </Box>

      <Button variant="contained" onClick={handleOpenDialog}>
        {t('Open {{mode}} Item Selector Dialog', {
          mode: isKioskMode ? 'Kiosk' : 'POS',
        })}
      </Button>

      {savedItemLists.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            {t('Selected Items History')}
          </Typography>
          {savedItemLists.map((selection, listIndex) => (
            <Card key={listIndex} sx={{ mb: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('Selection {{index}} ({{mode}})', {
                    index: listIndex + 1,
                    mode: selection.mode,
                  })}
                </Typography>
                {selection.items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('No items selected')}
                  </Typography>
                ) : (
                  <Box>
                    {selection.items.map((item, itemIndex) => (
                      <Box key={itemIndex} sx={{ py: 1 }}>
                        <Typography variant="body1">
                          <strong>
                            {(item as any).name ||
                              item.item?.name ||
                              'Unknown Item'}
                          </strong>
                          {(item as any).description && (
                            <span> - {(item as any).description}</span>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('Type: {{type}}', {
                            type:
                              item.itemType || (item as any).type || 'unknown',
                          })}
                        </Typography>
                        {itemIndex < selection.items.length - 1 && (
                          <Divider sx={{ mt: 1 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <PosConfigItemSelectorDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveItems}
        type={isKioskMode ? 'kiosk' : 'pos'}
      />
    </Box>
  );
};
